// pages/CartPage.js
import React, { useCallback, useEffect, useState } from 'react';
import CartSteps from './CartSteps';
import OrderSummary from './OrderSummary';
import CheckoutButtons from './CheckoutButtons';
import tw from 'twin.macro';
import { useSelector, useDispatch } from 'react-redux';
import { handleAddToCart, handleQuitarDeCarrito } from 'utils/carritoUtils';
import { calcularPrecioConDescuento } from 'utils/descuentoUtils';
import {
	decrementarCantidad,
	limpiarCarrito,
	quitarItem,
	setCostoEnvio,
	setDeliveryMethod,
	setDireccion,
	setNotas,
	setNotasCocinera,
} from 'store/carritoSlice';
import { toast } from 'react-toastify';
import { initMercadoPago } from '@mercadopago/sdk-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { crearPreferencia } from 'scripts/crearPreferenciaPagoMP';
import CheckoutStatusModal from '../StatusScreenModal';
import TransferenciaModal from '../TransferenciaModal';
import { finalizarPedido } from 'scripts/finalizarPedido';
import { usePaymentPolling } from 'hooks/usePaymentPolling';
import { ENVIO_GRATIS_MINIMO, ZONA_ENVIO_GRATIS } from 'constants/envios';

const EmptyCartButton = tw.button`px-8 py-3 font-bold rounded bg-primary-500 text-gray-100 hocus:bg-primary-700 hocus:text-gray-200 focus:shadow-outline focus:outline-none transition duration-300`;

const PageContainer = tw.div`flex flex-col min-h-128`;
const ContentContainer = tw.div`flex flex-col lg:flex-row flex-grow w-full p-6`;
const LeftColumn = tw.div`w-full lg:w-2/3 pr-0 lg:pr-6`;
const RightColumn = tw.div`w-full lg:w-1/3 p-6 lg:sticky lg:top-0 self-start mt-6 lg:mt-0`;
const NotasTextField = tw.textarea`w-full p-2 border rounded-lg mt-4`;
const TOAST_CHECKOUT_24H_ID = 'checkout-carrito-24h';

const CartPage = () => {
	const dispatch = useDispatch();
	const mpPublicKey = process.env.REACT_APP_MP_PUBLIC_KEY;
	const [step, setStep] = useState(1);
	const token = sessionStorage.getItem('token'); // Asegúrate de que el token esté en el store
	const user = useSelector((state) => state.usuario); // ID del cliente desde el store
	const [isLoading, setIsLoading] = useState(false); // Estado para el loader
	const [preferenceId, setPreferenceId] = useState(null);
	const notas = useSelector((state) => state.carrito.notas);
	const notasCocinera = useSelector((state) => state.carrito.notasCocinera || '');
	const cupon = useSelector((state) => state.carrito.cupon);
	const cuponId = cupon?.id;

	const deliveryMethod = useSelector((state) => state.carrito.deliveryMethod);
	const sucursalId = useSelector((state) => state.carrito.sucursalId);

	let userId = null;
	if (user.isLoggedIn) {
		userId = user.userInfo.id;
	}
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [paymentShownForOrder, setPaymentShownForOrder] = useState(null); // Rastrear si ya mostramos modal para este pedido

	useEffect(() => {
		if (!mpPublicKey) {
			console.error('REACT_APP_MP_PUBLIC_KEY no está configurada');
			return;
		}
		initMercadoPago(mpPublicKey, { locale: 'es-UY' });
	}, [mpPublicKey]);
	const [paymentId, setPaymentId] = useState(null);
	const [searchParams] = useSearchParams();

	useEffect(() => {
		const payment = searchParams.get('payment_id'); // Captura el parámetro "status" de la URL
		if (payment && payment !== 'null') {
			setPaymentId(payment);
			setIsModalOpen(true);
		}
		// Limpiar los parámetros de la URL
		const newUrl = window.location.origin + window.location.pathname; // Construye la nueva URL sin parámetros
		window.history.replaceState({}, document.title, newUrl); // Reemplaza la URL actual
	}, [searchParams]);

	// Get cart items
	const cartItems = useSelector((state) => state.carrito.items);

	// Get cart items
	const pedidoId = useSelector((state) => state.carrito.pedidoId);

	// Get products and packs from their respective slices
	const productosRedux = useSelector((state) => state.productos);
	const packsRedux = useSelector((state) => state.packs);

	const soloRecetarios =
		cartItems.length > 0 &&
		cartItems.every((item) => {
			if (item.tipo !== 'producto') {
				return false;
			}

			const categoriaItem = item.categoria?.titulo?.trim().toLowerCase();
			const productoRedux = productosRedux.find((prod) => prod.id === item.id);
			const categoriaRedux = productoRedux?.categoria?.titulo?.trim().toLowerCase();
			const categoriaTitulo = categoriaItem || categoriaRedux;

			return categoriaTitulo === 'recetarios';
		});
	const soloViandas = cartItems.length > 0 && cartItems.every((item) => item.tipo === 'vianda');
	const tieneViandas = cartItems.some((item) => item.tipo === 'vianda');

	// Function to get discounts based on item type
	const obtenerDescuentos = (item) => {
		if (item.tipo === 'producto') {
			const producto = productosRedux.find((prod) => prod.id === item.id);
			return producto ? producto.descuentos || [] : [];
		} else if (item.tipo === 'pack') {
			const pack = packsRedux.find((pck) => pck.id === item.id);
			return pack ? pack.descuentos || [] : [];
		}
		return [];
	};

	const costoEnvioCarrito = useSelector((state) => state.carrito.costoEnvio);

	const comisionMP = useSelector((state) => state.carrito.comisionMP);
	const direccionId = useSelector((state) => state.carrito.direccionId);
	// Calcular el subtotal de cada ítem, considerando si es producto o pack
	const calcularSubtotalItem = (item) => {
		if (item.tipo === 'pack') {
			// El precio del carrito es un snapshot del backend; no se recalcula con el catálogo.
			const tieneSnapshot = item.precioConAdicionalesCarrito != null;
			const precioBase =
				item.precioConAdicionalesCarrito ?? item.precioConAdicionales ?? item.precio_final ?? item.precio;
			const precioConDescuento = tieneSnapshot
				? Number(precioBase)
				: calcularPrecioConDescuento(precioBase, obtenerDescuentos(item));
			return precioConDescuento * item.cantidad;
		} else if (item.tipo === 'vianda') {
			return Number(item.precioUnitarioCarrito ?? item.precio ?? 0) * item.cantidad;
		} else {
			const precioConDescuento =
				item.precioUnitarioCarrito != null
					? Number(item.precioUnitarioCarrito)
					: calcularPrecioConDescuento(item.precio, obtenerDescuentos(item));
			const adicionalCosto = Number(item.adicional?.costoAdicionalCarrito ?? item.adicional?.costoAdicional ?? 0);
			return (precioConDescuento + adicionalCosto) * item.cantidad;
		}
	};
	const subtotal = cartItems.reduce((acc, item) => acc + calcularSubtotalItem(item), 0);

	// Obtener zona de envío desde Redux
	const zonaEnvioNombre = useSelector((state) => state.carrito.zonaEnvioNombre);

	// Aplicar lógica de envío gratis: subtotal > $2500 en zona gratis = envío gratis
	const costoEnvio =
		soloViandas ? 0
		: subtotal > ENVIO_GRATIS_MINIMO && zonaEnvioNombre === ZONA_ENVIO_GRATIS ? 0
		: costoEnvioCarrito;

	const handleIncrement = (item) => {
		handleAddToCart(item, 1, item.adicional?.id, token, userId, item.tipo, dispatch);
	};

	const handleDecrement = (item) => {
		handleQuitarDeCarrito(userId, item.id, 1, item.tipo, item.adicional?.id, token, item.viandasDiarias);
		dispatch(decrementarCantidad(item));
	};

	const handleQuitarCarrito = (item) => {
		handleQuitarDeCarrito(
			userId,
			item.id,
			item.cantidad,
			item.tipo,
			item.adicional?.id,
			token,
			item.viandasDiarias,
		);
		dispatch(quitarItem(item));
	};

	const handleContinueFromCart = () => {
		toast.info('Recordatorio: tu pedido/carrito se guarda por 24 horas y luego se limpia automáticamente.', {
			toastId: TOAST_CHECKOUT_24H_ID,
		});

		if (soloRecetarios) {
			dispatch(setDeliveryMethod('pickup'));
			dispatch(setDireccion(0));
			dispatch(setCostoEnvio(0));
			setStep(4);
			return;
		}
		if (soloViandas) {
			dispatch(setDeliveryMethod('delivery'));
			dispatch(setCostoEnvio(0));
			setStep(3);
			return;
		}

		setStep(2);
	};

	useEffect(() => {
		if (soloViandas && deliveryMethod !== 'delivery') {
			dispatch(setDeliveryMethod('delivery'));
		}
		if (soloViandas && costoEnvioCarrito !== 0) {
			dispatch(setCostoEnvio(0));
		}
	}, [soloViandas, deliveryMethod, costoEnvioCarrito, dispatch]);

	const [fechaSeleccionada, setFechaSeleccionada] = useState(new Date());
	const [fechaFormateada, setFechaFormateada] = useState(null);

	useEffect(() => {
		setFechaFormateada(format(fechaSeleccionada, 'yyyy-MM-dd'));
	}, [fechaSeleccionada]);

	const handleMercadoPago = useCallback(async () => {
		setIsLoading(true); // Mostrar loader mientras se genera la preferencia
		try {
			const data = await crearPreferencia(pedidoId, direccionId, fechaFormateada, deliveryMethod, cuponId, token);
			if (data && data.preference_id) {
				setPreferenceId(data.preference_id);
			} else {
				toast.error('Error al generar la preferencia de pago.');
			}
		} catch (error) {
			console.error('Error:', error);
			toast.error(error?.message || 'Ocurrió un error al procesar el pago.');
		} finally {
			setIsLoading(false); // Ocultar loader una vez que se complete la solicitud
		}
	}, [pedidoId, direccionId, fechaFormateada, deliveryMethod, cuponId, token]);
	const [metodoPago, setMetodoPago] = useState('');
	const [mostrarModalTransferencia, setMostrarModalTransferencia] = useState(false); // Estado para mostrar el modal

	const handleFinalizarCompraTransferencia = () => {
		setMostrarModalTransferencia(true);
	};

	const handleFinalizarCompraEfectivo = () => {
		const fetchFinalizarCompraEfectivo = async () => {
			await finalizarPedido(
				pedidoId,
				3,
				'',
				direccionId,
				fechaFormateada,
				deliveryMethod,
				notas,
				notasCocinera,
				cuponId,
				token,
				sucursalId,
			)
				.then(() => {
					toast.success('¡Gracias por tu compra!');
					dispatch(limpiarCarrito());
				})
				.catch((e) => {
					console.error('Error al finalizar la compra:', e);
					toast.error('Ocurrió un error al finalizar la compra.');
				});
		};
		fetchFinalizarCompraEfectivo();
	};

	const handleNotasChange = (event) => {
		dispatch(setNotas(event.target.value));
	};

	const handleNotasCocineraChange = (event) => {
		dispatch(setNotasCocinera(event.target.value));
	};

	const navigate = useNavigate();

	// 🔄 Sistema de Polling para detectar pagos completados vía webhook
	const { isPolling } = usePaymentPolling(pedidoId, token, {
		enabled: !!pedidoId && cartItems.length > 0, // Solo activar si hay pedido y items en carrito
		interval: 5000, // Consultar cada 5 segundos
		timeout: 600000, // Timeout de 10 minutos
		onPagado: (data) => {
			console.log('✅ Pago detectado por polling:', data);
			// Solo mostrar modal si no lo hemos mostrado ya para este pedido
			if (data.payment_id && paymentShownForOrder !== pedidoId) {
				setPaymentId(data.payment_id);
				setIsModalOpen(true);
				setPaymentShownForOrder(pedidoId); // Marcar que ya mostramos el modal para este pedido
				toast.success('¡Pago confirmado! Tu pedido fue procesado correctamente.');
			}
		},
		onError: (error) => {
			console.error('Error en polling:', error);
			// Solo mostrar error si no es de sesión expirada (ya se maneja en el hook)
			if (error.message !== 'SESION_EXPIRADA') {
				// No mostrar toast para no molestar al usuario, solo logear
			}
		},
		onTimeout: () => {
			console.log('⏱️ Timeout de polling alcanzado. El usuario puede refrescar la página.');
			// No mostrar toast, el usuario puede estar en otra pestaña
		},
	});

	// Log para debugging (opcional, se puede remover en producción)
	useEffect(() => {
		if (isPolling) {
			console.log('🔄 Polling activo para pedido:', pedidoId);
		}
	}, [isPolling, pedidoId]);

	return (
		<PageContainer>
			{cartItems.length > 0 ?
				<ContentContainer>
					<LeftColumn>
						<CartSteps
							cartItems={cartItems}
							costoEnvio={costoEnvio}
							comisionMP={comisionMP}
							step={step}
							setStep={setStep}
							metodoPago={metodoPago}
							direccionId={direccionId}
							setMetodoPago={setMetodoPago}
							handleMercadoPago={handleMercadoPago}
							handleIncrement={handleIncrement}
							handleDecrement={handleDecrement}
							handleQuitarCarrito={handleQuitarCarrito}
							setIsLoading={setIsLoading}
							preferenceId={preferenceId}
							setPreferenceId={setPreferenceId}
							obtenerDescuentos={obtenerDescuentos}
							calcularSubtotalItem={calcularSubtotalItem}
							calcularPrecioConDescuento={calcularPrecioConDescuento}
							fechaSeleccionada={fechaSeleccionada}
							setFechaSeleccionada={setFechaSeleccionada}
							deliveryMethod={deliveryMethod}
							soloRecetarios={soloRecetarios}
							soloViandas={soloViandas}
							tieneViandas={tieneViandas}
							onContinueFromCart={handleContinueFromCart}
						/>
					</LeftColumn>
					<RightColumn>
						<OrderSummary
							subtotal={subtotal}
							costoEnvio={costoEnvio}
							comisionMP={comisionMP}
							cupon={cupon}
							step={step}
							metodoPago={metodoPago}
							soloViandas={soloViandas}
						/>
						<NotasTextField
							placeholder="Notas para la cocinera: ej (sin sal agregada)"
							rows={4}
							value={notasCocinera}
							onChange={handleNotasCocineraChange}
						/>
						<NotasTextField
							placeholder="Notas del Pedido: ej (no funciona el timbre)"
							rows={4}
							value={notas}
							onChange={handleNotasChange}
						/>
						<CheckoutButtons
							isLoading={isLoading}
							metodoPago={metodoPago}
							preferenceId={preferenceId}
							setIsLoading={setIsLoading}
							setPreferenceId={setPreferenceId}
							handleFinalizarCompraTransferencia={handleFinalizarCompraTransferencia}
							handleFinalizarCompraEfectivo={handleFinalizarCompraEfectivo}
						/>
					</RightColumn>
					<TransferenciaModal
						mostrarModalTransferencia={mostrarModalTransferencia}
						setMostrarModalTransferencia={setMostrarModalTransferencia}
						pedidoId={pedidoId}
						direccionId={direccionId}
						fechaEnvio={fechaFormateada}
						deliveryMethod={deliveryMethod}
						notas={notas}
						notasCocinera={notasCocinera}
						cuponId={cuponId}
						token={token}
						dispatch={dispatch}
						sucursalId={sucursalId}
					/>
					<CheckoutStatusModal
						payment={paymentId}
						isModalOpen={isModalOpen}
						setIsModalOpen={setIsModalOpen}
						token={token}
						pedidoId={pedidoId}
						direccionId={direccionId}
						fechaEnvio={fechaFormateada}
						deliveryMethod={deliveryMethod}
						notas={notas}
						notasCocinera={notasCocinera}
						cuponId={cuponId}
						dispatch={dispatch}
						sucursalId={sucursalId}
					/>
				</ContentContainer>
			:	<div tw="flex flex-row items-center justify-center w-full min-h-[60vh] gap-4">
					<h2 tw="text-2xl font-bold text-gray-800">Tu carrito está vacío</h2>
					<EmptyCartButton onClick={() => navigate('/productos')}>Ver Productos</EmptyCartButton>
				</div>
			}
		</PageContainer>
	);
};

export default CartPage;
